/**
 * DecorPackagesLightbox — full-screen slideshow that opens when a customer
 * clicks any decor-package thumbnail under a Decoration Team pro card.
 *
 * Now displays:
 *   - the package's cover image
 *   - any additional media (photos + ≤30s videos) the pro attached
 * Customer can swipe / arrow-key through all media for the SAME package
 * (cover first, then media in upload order). Prev/Next at the end also moves
 * to the next package.
 */
import React, { useEffect, useRef, useState } from 'react';
import { X, CaretLeft, CaretRight } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND_URL}${p || ''}`);

// Flatten a single package into a list of slides: [cover, ...media].
const slidesFor = (pkg) => {
  if (!pkg) return [];
  const slides = [];
  if (pkg.image) {
    slides.push({ kind: 'image', url: pkg.image, poster: null, duration: null, _cover: true });
  }
  (pkg.media || []).forEach((m) => slides.push({ ...m, _cover: false }));
  return slides;
};

const DecorPackagesLightbox = ({ open, packages, startIdx = 0, onClose, formatPrice }) => {
  const [pkgIdx, setPkgIdx] = useState(startIdx);
  const [slideIdx, setSlideIdx] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPkgIdx(startIdx);
      setSlideIdx(0);
    }
  }, [open, startIdx]);

  // Pause and reset videos when slide changes.
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [pkgIdx, slideIdx]);

  const pkg = packages?.[pkgIdx];
  const slides = slidesFor(pkg);
  const slide = slides[slideIdx];

  const next = () => {
    if (slideIdx < slides.length - 1) {
      setSlideIdx((i) => i + 1);
    } else {
      // Jump to first slide of next package (wrap to start).
      setPkgIdx((i) => (i + 1) % packages.length);
      setSlideIdx(0);
    }
  };
  const prev = () => {
    if (slideIdx > 0) {
      setSlideIdx((i) => i - 1);
    } else {
      // Jump to last slide of previous package (wrap).
      const newPkg = (pkgIdx - 1 + packages.length) % packages.length;
      setPkgIdx(newPkg);
      const prevSlides = slidesFor(packages[newPkg]);
      setSlideIdx(Math.max(0, prevSlides.length - 1));
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, slideIdx, pkgIdx, packages.length]);

  if (!open || !packages || packages.length === 0 || !pkg) return null;

  const priceText = formatPrice ? formatPrice(pkg.price) : `₹${(pkg.price || 0).toLocaleString('en-IN')}`;
  const totalSlides = slides.length || 1;

  return (
    <div className="fixed inset-0 bg-black/85 z-[200] flex flex-col items-center justify-center p-4"
      data-testid="decor-lightbox" onClick={onClose}>
      <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
          className="absolute -top-3 -right-3 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg z-10"
          aria-label="Close" data-testid="decor-lightbox-close">
          <X size={18} weight="bold" />
        </button>

        <div className="bg-white rounded-md overflow-hidden">
          {/* Media area */}
          <div className="relative bg-sand-100 aspect-[4/3] flex items-center justify-center">
            {slide?.kind === 'video' ? (
              <video ref={videoRef} src={fullUrl(slide.url)}
                poster={slide.poster ? fullUrl(slide.poster) : undefined}
                className="max-h-full max-w-full" controls autoPlay
                data-testid="decor-lightbox-video" />
            ) : slide ? (
              <img src={fullUrl(slide.url)} alt={pkg.name} className="w-full h-full object-cover"
                data-testid="decor-lightbox-image" />
            ) : (
              <span className="text-text-secondary">No image</span>
            )}
            {(totalSlides > 1 || packages.length > 1) && (
              <>
                <button onClick={prev}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow"
                  aria-label="Previous" data-testid="decor-lightbox-prev">
                  <CaretLeft size={18} weight="bold" />
                </button>
                <button onClick={next}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow"
                  aria-label="Next" data-testid="decor-lightbox-next">
                  <CaretRight size={18} weight="bold" />
                </button>
              </>
            )}
            {/* Slide counter */}
            <div className="absolute bottom-2 right-2 bg-black/70 text-sand-50 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded">
              {slideIdx + 1} / {totalSlides}
              {slide?.kind === 'video' && slide.duration ? ` · ${Math.round(slide.duration)}s` : ''}
            </div>
          </div>

          {/* Caption */}
          <div className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="min-w-0">
              <div className="font-bold text-base truncate" data-testid="decor-lightbox-name">{pkg.name}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-text-secondary mt-0.5">
                {pkg.event_kind === 'wedding' ? 'Wedding-tier events' : 'Other events'}
                {slides.length > 1 && (
                  <span className="ml-2 normal-case font-normal">· {slides.length - 1} extra photo/video{slides.length > 2 ? 's' : ''}</span>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-text-secondary">Price</div>
              <div className="text-xl font-bold text-brand-primary" data-testid="decor-lightbox-price">{priceText}</div>
            </div>
          </div>

          {/* Package picker thumb strip — one thumb per package */}
          {packages.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto p-2 border-t border-[#E8E1D5] bg-sand-50">
              {packages.map((tp, ti) => (
                <button key={tp.id || ti} type="button"
                  onClick={() => { setPkgIdx(ti); setSlideIdx(0); }}
                  className={`flex-shrink-0 w-14 h-14 bg-sand-100 border-2 overflow-hidden ${
                    ti === pkgIdx ? 'border-brand-primary' : 'border-transparent'
                  }`} data-testid={`decor-lightbox-thumb-${ti}`}>
                  {tp.image && (
                    <img src={fullUrl(tp.image)} alt="" className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DecorPackagesLightbox;
